import React from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { Trophy, Users, Target, Award, Zap, Heart, Star } from 'lucide-react';
import { urlEdizioneVista } from '../links';
import { getMiglioriGiocatori } from './util';
import ClassificaGenerale from './ClassificaGenerale';
import ClassificaCard from '../../../components/statistiche/ClassificaCard';
import { LinkGiocatore } from '../../../components/statistiche/Link';
import '../Statistiche.css';

/**
 * Il riepilogo di una edizione: la rotta `index` sotto `EdizioneLayout`.
 *
 * Prima era la vista `overview` di una macchina a stati, e le sue tre viste figlie erano altri
 * valori della stessa variabile: nessuna aveva un indirizzo, quindi niente era condivisibile e il
 * tasto Indietro usciva dalla pagina invece di risalire. Ora le tile sono link, non setState.
 *
 * Le statistiche arrivano dal layout: sono le stesse che servono alle viste sorelle, e scaricarle
 * di nuovo a ogni passaggio fra loro era il costo che il layout esiste per evitare.
 */

const Tile = ({ icon: Icon, titolo, valore, colore, to }) => (
  <Link className={`statistiche-stat-card ${colore}`} to={to}>
    <div className="statistiche-stat-card-content">
      <Icon className={`statistiche-stat-card-icon ${colore}`} />
      <h3 className="statistiche-stat-card-title">{titolo}</h3>
      <p className="statistiche-stat-card-value">{valore}</p>
    </div>
  </Link>
);

/**
 * Dal giocatore di una edizione alla voce che il riquadro condiviso sa disporre. Il nome è un
 * link: da quando il DTO porta l'id dell'anagrafica, da una classifica si salta alla carriera.
 */
const vociClassifica = (giocatori, stat) => (giocatori ?? []).map((g, i) => ({
  chiave: g.idGiocatore ?? `${g.cognome}-${i}`,
  nome: <LinkGiocatore id={g.idGiocatore} nome={g.nome} cognome={g.cognome} />,
  contesto: g.squadra || g.maglia,
  valore: g[stat],
}));

export default function Edizione() {
  const { statistiche, idEdizione } = useOutletContext();

  const somma = (campo) => statistiche.giocatori?.reduce((s, p) => s + (p[campo] || 0), 0) || 0;
  const vista = (nome) => urlEdizioneVista(idEdizione, nome);
  const giocatoriOrdinatiPer = (stat) => `${vista('giocatori')}?ordina=${stat}`;

  return (
    <>
      <div className="statistiche-stats-grid">
        <Tile icon={Users} titolo="Squadre" valore={statistiche.squadre?.length || 0} colore="blue" to={vista('squadre')} />
        <Tile icon={Users} titolo="Giocatori" valore={statistiche.giocatori?.length || 0} colore="green" to={vista('giocatori')} />
        <Tile icon={Target} titolo="Partite" valore={statistiche.partite?.length || 0} colore="purple" to={vista('partite')} />
        <Tile icon={Award} titolo="Mete" valore={somma('mete')} colore="orange" to={giocatoriOrdinatiPer('mete')} />
        <Tile icon={Target} titolo="Pieni" valore={somma('pieni')} colore="red" to={giocatoriOrdinatiPer('pieni')} />
        <Tile icon={Target} titolo="Vuoti" valore={somma('vuoti')} colore="yellow" to={giocatoriOrdinatiPer('vuoti')} />
        <Tile icon={Award} titolo="Assist" valore={somma('assist')} colore="indigo" to={giocatoriOrdinatiPer('assist')} />
      </div>

      <div className="statistiche-section">
        <div className="statistiche-section-header">
          <Trophy className="statistiche-section-icon" />
          <h2 className="statistiche-section-title">Classifica Generale</h2>
        </div>
        <ClassificaGenerale edizioneId={idEdizione} statistiche={statistiche} />
      </div>

      <div className="statistiche-section statistiche-personal-rankings">
        <div className="statistiche-section-header">
          <Star className="statistiche-section-icon" />
          <h2 className="statistiche-section-title">Classifiche Personali</h2>
        </div>
        <div className="classifiche-griglia">
          <ClassificaCard titolo="Miglior Realizzatore" icona={Award} tono="realizzatore"
            voci={vociClassifica(getMiglioriGiocatori(statistiche.giocatori, 'mete', 'm', 5), 'mete')} />
          <ClassificaCard titolo="Miglior Realizzatrice" icona={Award} tono="realizzatrice"
            voci={vociClassifica(getMiglioriGiocatori(statistiche.giocatori, 'mete', 'f', 5), 'mete')} />
          <ClassificaCard titolo="Miglior Scalpatore" icona={Target} tono="scalpatore"
            voci={vociClassifica(getMiglioriGiocatori(statistiche.giocatori, 'pieni', 'm', 5), 'pieni')} />
          <ClassificaCard titolo="Miglior Scalpatrice" icona={Target} tono="scalpatrice"
            voci={vociClassifica(getMiglioriGiocatori(statistiche.giocatori, 'pieni', 'f', 5), 'pieni')} />
          <ClassificaCard titolo="Miglior Regista M" icona={Zap} tono="regista-m"
            voci={vociClassifica(getMiglioriGiocatori(statistiche.giocatori, 'assist', 'm', 5), 'assist')} />
          <ClassificaCard titolo="Miglior Regista F" icona={Heart} tono="regista-f"
            voci={vociClassifica(getMiglioriGiocatori(statistiche.giocatori, 'assist', 'f', 5), 'assist')} />
        </div>
      </div>
    </>
  );
}
